import { ComponentType, Dispatch, SetStateAction } from 'react';
import { Control, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormControl, MenuItem, Select, Stack, Typography } from '@mui/material';
import {
  ProgramWizardData,
  ProgramWizardLoadshareMasterType,
  ProgramWizardNumberFieldProps,
  ProgramWizardOperatingMode,
} from '../ProgramWizard.types';

const MODE_TEXT_KEYS: Partial<Record<ProgramWizardOperatingMode, string>> = {
  [ProgramWizardOperatingMode.GRID]: 'inputSettings.modeText.grid',
  [ProgramWizardOperatingMode.GENSETS]: 'inputSettings.modeText.gensets',
  [ProgramWizardOperatingMode.GEN_SET_CYCLE]: 'inputSettings.modeText.gensetCycle',
  [ProgramWizardOperatingMode.LOADSHARE]: 'inputSettings.modeText.loadshare',
};

type StepInputSettingsProps = {
  wizardData: ProgramWizardData;
  setWizardData: Dispatch<SetStateAction<ProgramWizardData>>;
  NumberFieldComponent: ComponentType<ProgramWizardNumberFieldProps>;
  control: Control<FieldValues>;
};

const StepInputSettings = ({ wizardData, setWizardData, NumberFieldComponent, control }: StepInputSettingsProps) => {
  const { t } = useTranslation('programWizard');
  const handleChangeLoadshareMasterType = (val: { target: { value: string } }) => {
    setWizardData((wData) => ({
      ...wData,
      loadShareMasterType: val.target.value as ProgramWizardData['loadShareMasterType'],
    }));
  };

  const modeText = (mode: ProgramWizardOperatingMode | null) => {
    const key = mode !== null ? MODE_TEXT_KEYS[mode] : undefined;
    return key ? t(key) : '';
  };

  return (
    <>
      <Typography variant="h4">{modeText(wizardData.operatingMode)}</Typography>
      <Stack direction="row" spacing={3} mb={2}>
        <Stack spacing={2} sx={{ flex: 1 }}>
          <Typography>{t('inputSettings.maxInputCurrent')}</Typography>
          <NumberFieldComponent
            label={`${t('inputSettings.maxInputCurrentLabel')}*`}
            name="maxInputCurrent"
            minValue={20}
            maxValue={400}
            defaultValue={wizardData.maxInputCurrent}
            required={true}
            control={control}
          />
        </Stack>
        {(wizardData.operatingMode === ProgramWizardOperatingMode.GENSETS ||
          wizardData.operatingMode === ProgramWizardOperatingMode.GEN_SET_CYCLE) && (
          <Stack spacing={2} sx={{ flex: 1 }}>
            <Typography>{t('inputSettings.ratedPower')}</Typography>
            <NumberFieldComponent
              label={`${t('inputSettings.ratedPowerLabel')}*`}
              name="genSetData1"
              minValue={40}
              maxValue={1000}
              defaultValue={wizardData.genSetData[1]}
              required={true}
              control={control}
            />
          </Stack>
        )}
        {wizardData.operatingMode === ProgramWizardOperatingMode.LOADSHARE && (
          <Stack spacing={2} sx={{ flex: 1 }}>
            <Typography>{t('inputSettings.loadshareMasterType')}</Typography>
            <FormControl variant="outlined" size="small" fullWidth>
              <Select
                labelId="select-loadshare-master-type"
                value={wizardData.loadShareMasterType || ProgramWizardLoadshareMasterType.NONE}
                onChange={handleChangeLoadshareMasterType}
              >
                <MenuItem value={ProgramWizardLoadshareMasterType.WEAK_GRID}>{t('inputSettings.weakGrid')}</MenuItem>
                <MenuItem value={ProgramWizardLoadshareMasterType.STRONG_GRID}>
                  {t('inputSettings.strongGrid')}
                </MenuItem>
                <MenuItem value={ProgramWizardLoadshareMasterType.NONE}>{t('inputSettings.none')}</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        )}
      </Stack>
    </>
  );
};

export default StepInputSettings;
